import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import TextLink from 'LuxKit/TextLink'
import SolidChartIcon from 'LuxKit/Icons/solid/SolidChartIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import IconButton from 'LuxKit/Button/IconButton'
import Group from 'components/utils/Group'
import Select from 'components/Common/Form/Input/Select'
import { setChertoExperiment } from 'actions/ChertoActions'
import type { ExperimentConfig } from 'constants/experiments'
import {
  formatExperimentTitle,
  getChertoExperimentCohortKeys,
  getChertoExperimentReportUrl,
  getChertoSelectedCohort,
} from './ExperimentPanelHelpers'

interface Props {
  experimentId: string
  experiment: App.ChertoExperimentDetails | undefined
  experimentConfig: ExperimentConfig
}

function ChertoExperimentItem({ experimentId, experiment, experimentConfig }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const experimentCohorts = useMemo(
    () => getChertoExperimentCohortKeys(experimentConfig.variations),
    [experimentConfig.variations],
  )
  const selectedCohort = useMemo(
    () => getChertoSelectedCohort(experiment, experimentCohorts),
    [experiment, experimentCohorts],
  )
  const reportUrl = useMemo(() => getChertoExperimentReportUrl(experimentId), [experimentId])

  const handleCohortChange = useCallback<React.ChangeEventHandler<HTMLSelectElement>>((e) => {
    const variationKey = e.target.value
    const enabled = variationKey !== 'off'

    return dispatch(setChertoExperiment(experimentId, {
      provider: 'cherto',
      variationKey,
      enabled,
      triggered: experiment?.triggered ?? false,
      experimentId,
    }, true))
  }, [dispatch, experiment?.triggered, experimentId])

  const experimentTitle = useMemo(() => formatExperimentTitle(experimentId), [experimentId])

  return (
    <Group direction="vertical" gap={20}>
      <Group direction="vertical" gap={4}>
        <TextLink
          underline={false}
          href={reportUrl}
          target="_blank"
          weight="regular"
        >
          {experimentTitle}
        </TextLink>
        <BodyText variant="small" colour="light-foreground-alt">
          {experimentId}
        </BodyText>
      </Group>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" style={{ width: '100%' }}>
        <Group direction="horizontal" gap={4} verticalAlign="center">
          <BodyText variant="medium" colour="light-foreground-alt">
            <i>{intl.formatMessage({
              id: 'temp_cherto_experiment_item_type_label',
              defaultMessage: 'Cherto experiment',
              description: 'Type label shown for a Cherto experiment entry in the internal AB control panel',
            })}
            </i>
          </BodyText>
          <IconButton
            kind="tertiary"
            size="small"
            href={reportUrl}
            target="_blank"
          >
            <SolidChartIcon />
          </IconButton>
        </Group>
        <Select
          label=""
          name={`${experimentId}-cohort`}
          value={selectedCohort}
          onChange={handleCohortChange}
          noValidationMessage
          optionalIndicatorEnabled={false}
        >
          {experimentCohorts.map((cohort) => (
            <option value={cohort} key={cohort}>
              {cohort}
            </option>
          ))}
        </Select>
      </Group>
    </Group>
  )
}

export default ChertoExperimentItem
